import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Maximize2, Minimize2 } from "lucide-react";
import { SlideshowTitleCard } from "@wedding/slideshow-ui";
import type { SlideState } from "@wedding/shared";
import { cn } from "../../lib/utils";
import { cdnUrl } from "../../lib/cdn";

const RING_SIZE = 44;
const RING_STROKE = 3;
const RING_R = (RING_SIZE - RING_STROKE) / 2;
const RING_C = 2 * Math.PI * RING_R;

/**
 * rAF-driven slide-time ring: progress derives from the SERVER timeline
 * (startedAt + durationMs + clock offset), so it matches every Sahne screen.
 */
function ProgressRing({
  startedAt,
  durationMs,
  paused,
  clockOffsetRef,
}: {
  startedAt: number;
  durationMs: number;
  paused: boolean;
  clockOffsetRef: React.MutableRefObject<number>;
}) {
  const circleRef = useRef<SVGCircleElement | null>(null);

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const el = circleRef.current;
      if (el && durationMs > 0) {
        const elapsed = Date.now() + clockOffsetRef.current - startedAt;
        const p = Math.min(1, Math.max(0, elapsed / durationMs));
        el.style.strokeDashoffset = `${RING_C * (1 - p)}`;
      }
      if (!paused) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [startedAt, durationMs, paused, clockOffsetRef]);

  return (
    <svg width={RING_SIZE} height={RING_SIZE} className="-rotate-90" aria-hidden>
      <circle
        cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_R}
        fill="none" stroke="currentColor" strokeWidth={RING_STROKE}
        className="text-white/15"
      />
      <circle
        ref={circleRef}
        cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_R}
        fill="none" stroke="currentColor" strokeWidth={RING_STROKE} strokeLinecap="round"
        strokeDasharray={RING_C} strokeDashoffset={RING_C}
        className="text-primary"
      />
    </svg>
  );
}

interface RejiPreviewProps {
  slideState: SlideState | null;
  clockOffsetRef: React.MutableRefObject<number>;
  weddingTitle: string;
}

/**
 * Live monitor of what every Sahne screen shows right now, with the slide-time
 * ring. Tap → fullscreen watch mode ("Sahneye katıl") — chrome hides.
 */
function RejiPreviewImpl({ slideState, clockOffsetRef, weddingTitle }: RejiPreviewProps) {
  const { t } = useTranslation();
  const [watch, setWatch] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const media = slideState?.media ?? null;
  const isCard = slideState?.slideType === "card";
  const paused = slideState?.paused ?? false;
  const idle = slideState?.idle ?? false;
  const hasShow = (slideState?.total ?? 0) > 0;

  // Watch mode: best-effort native fullscreen, sync exit via fullscreenchange
  useEffect(() => {
    if (watch) {
      containerRef.current?.requestFullscreen?.().catch(() => {});
    } else if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    }
    const onChange = () => {
      if (!document.fullscreenElement) setWatch(false);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [watch]);

  // Server-synchronized video pause + best-effort offset seek
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) v.pause();
    else v.play().catch(() => {});
  }, [paused, media?.id]);

  // Hold the last media url so the title-card cover-bleed matches the guest
  // interstitial (which bleeds the held slide underneath the card).
  const [lastMediaUrl, setLastMediaUrl] = useState<string | null>(null);
  useEffect(() => {
    if (media?.url) setLastMediaUrl(media.url);
  }, [media?.url]);

  return (
    <div
      ref={containerRef}
      onClick={() => setWatch((w) => !w)}
      role="button"
      aria-label={watch ? t("reji.exitWatch") : t("reji.watchMode")}
      className={cn(
        "relative overflow-hidden bg-black/60 cursor-pointer group",
        watch
          ? "fixed inset-0 z-[60] rounded-none"
          : "rounded-2xl border border-white/10 shadow-2xl shadow-black/50 ring-1 ring-white/5",
      )}
    >
      <div className={cn("relative w-full", watch ? "h-full" : "aspect-video")}>
        {!hasShow || !slideState ? (
          <div className="absolute inset-0 flex items-center justify-center text-white/40 text-sm px-6 text-center">
            {slideState ? t("reji.emptyPlaylist") : t("reji.connecting")}
          </div>
        ) : isCard || !media ? (
          // Shared title-card — identical to the guest Sahne (minus QR/date).
          <SlideshowTitleCard
            title={weddingTitle}
            coverUrl={lastMediaUrl ? cdnUrl(lastMediaUrl, { w: 64 }) : null}
          />
        ) : media.kind === "image" ? (
          <img
            key={media.id}
            src={cdnUrl(media.url, { w: 800 })}
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
          />
        ) : (
          <video
            key={media.id}
            ref={videoRef}
            src={media.url}
            muted
            playsInline
            autoPlay={!paused}
            preload="auto"
            className="absolute inset-0 w-full h-full object-contain"
            onLoadedMetadata={(e) => {
              // Best-effort sync to the shared timeline (monitor-grade)
              const v = e.currentTarget;
              const offset = Date.now() + clockOffsetRef.current - (slideState?.startedAt ?? Date.now());
              if (offset > 500 && Number.isFinite(v.duration)) {
                v.currentTime = Math.min(offset / 1000, Math.max(0, v.duration - 0.1));
              } else if (paused && Number.isFinite(v.duration)) {
                v.currentTime = 0.04; // paint a frame while paused (else black)
              }
            }}
          />
        )}

        {/* Slide-time ring (hidden while idle / empty) */}
        {hasShow && slideState && !idle && (
          <div className="absolute top-3 right-3 drop-shadow-lg">
            <ProgressRing
              startedAt={slideState.startedAt}
              durationMs={slideState.durationMs}
              paused={paused}
              clockOffsetRef={clockOffsetRef}
            />
          </div>
        )}

        {/* Watch-mode affordance */}
        <div
          className={cn(
            "absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/15 px-3 py-1.5 text-white/85 text-xs font-medium",
            "opacity-0 group-hover:opacity-100 transition-opacity",
            watch && "opacity-100",
          )}
        >
          {watch ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          {watch ? t("reji.exitWatch") : t("reji.watchMode")}
        </div>
      </div>
    </div>
  );
}

export const RejiPreview = memo(RejiPreviewImpl);
